import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Users, CheckCircle, RotateCcw } from "lucide-react";

interface SortingWorksheetProps {
  content: any;
  onProgressUpdate: (progress: number, score?: number) => void;
  showPrintView?: boolean;
}

const SortingWorksheet = ({ content, onProgressUpdate, showPrintView = false }: SortingWorksheetProps) => {
  const [categories, setCategories] = useState(content.items || []);
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [unplacedItems, setUnplacedItems] = useState<any[]>([]);

  useEffect(() => {
    // Initialize unplaced items from all categories with unique identifiers
    const allItems = categories.flatMap((category: any, catIndex: number) => 
      category.items.map((item: any, itemIndex: number) => ({
        ...item,
        originalCategory: category.name,
        uniqueId: `${catIndex}-${itemIndex}-${item.name}` // Add unique identifier
      }))
    );
    setUnplacedItems(allItems.sort(() => Math.random() - 0.5)); // Shuffle items
    
    // Clear items from categories initially
    setCategories(categories.map((cat: any) => ({
      ...cat,
      items: []
    })));
  }, []);

  useEffect(() => {
    const totalItems = content.items.reduce((total: number, cat: any) => total + cat.items.length, 0);
    const placedItems = categories.reduce((total: number, cat: any) => total + cat.items.length, 0);
    const correctlyPlaced = categories.reduce((total: number, cat: any) => {
      return total + cat.items.filter((item: any) => item.originalCategory === cat.name).length;
    }, 0);
    
    const progress = (placedItems / totalItems) * 100;
    const score = placedItems > 0 ? (correctlyPlaced / placedItems) * 100 : 100;
    
    onProgressUpdate(progress, Math.round(score));
  }, [categories, content.items, onProgressUpdate]);

  const handleDragStart = (e: React.DragEvent, item: any, source: 'unplaced' | string) => {
    setDraggedItem({ ...item, source });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetCategory: string) => {
    e.preventDefault();
    
    if (!draggedItem) return;

    // Remove item from source using proper identification
    if (draggedItem.source === 'unplaced') {
      setUnplacedItems(prev => prev.filter(item => 
        // Compare using multiple properties to ensure accurate matching
        !(item.name === draggedItem.name && 
          item.originalCategory === draggedItem.originalCategory &&
          item.uniqueId === draggedItem.uniqueId)
      ));
    } else {
      setCategories(prev => prev.map(cat => 
        cat.name === draggedItem.source 
          ? { 
              ...cat, 
              items: cat.items.filter((item: any) => 
                !(item.name === draggedItem.name && 
                  item.originalCategory === draggedItem.originalCategory &&
                  item.uniqueId === draggedItem.uniqueId)
              ) 
            }
          : cat
      ));
    }

    // Add item to target category
    setCategories(prev => prev.map(cat => 
      cat.name === targetCategory
        ? { ...cat, items: [...cat.items, draggedItem] }
        : cat
    ));

    setDraggedItem(null);
  };

  const resetGame = () => {
    // Get all items currently placed in categories
    const allPlacedItems = categories.flatMap((category: any) => category.items);
    
    // Get original items from content with unique identifiers
    const originalItems = content.items.flatMap((category: any, catIndex: number) => 
      category.items.map((item: any, itemIndex: number) => ({
        ...item,
        originalCategory: category.name,
        uniqueId: `${catIndex}-${itemIndex}-${item.name}`
      }))
    );
    
    // Combine and shuffle all items
    setUnplacedItems([...originalItems].sort(() => Math.random() - 0.5));
    
    // Clear all categories
    setCategories(content.items.map((cat: any) => ({
      ...cat,
      items: []
    })));
  };

  // If in print view, render the printable version
  if (showPrintView) {
    const allItems = content.items.flatMap((category: any) => 
      category.items.map((item: any) => item.name)
    ).sort(() => Math.random() - 0.5);
    
    return (
      <div className="print-worksheet bg-white p-6">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2">{content.title}</h2>
          <p className="text-gray-600 text-sm mb-4">{content.instructions}</p>
        </div>
        
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-3">Items to Sort:</h3>
          <div className="border-2 border-dashed border-gray-400 p-3 rounded">
            <div className="grid grid-cols-3 gap-2">
              {allItems.map((item, index) => (
                <div key={index} className="border border-gray-300 p-2 text-center text-sm rounded">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          {content.items.map((category: any, index: number) => (
            <div key={index} className="border-2 border-gray-400 rounded p-4">
              <h4 className="text-lg font-bold text-center mb-4 bg-gray-100 p-2 rounded">
                {category.name}
              </h4>
              <div className="space-y-2">
                {[...Array(Math.max(3, category.items.length))].map((_, itemIndex) => (
                  <div key={itemIndex} className="border-b border-gray-300 h-8"></div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-4 border-t border-gray-400 text-center text-sm text-gray-600">
          <p>Student Name: ________________________  Date: ___________</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="border-2 border-orange-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-orange-700 flex items-center gap-2">
              <Users className="h-6 w-6" />
              {content.title}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={resetGame}
              className="text-orange-600 border-orange-300"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          </div>
          <p className="text-gray-600">{content.instructions}</p>
        </CardHeader>
        
        <CardContent>
          {/* Unplaced Items */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-700 mb-3">Drag these items to the correct categories:</h3>
            <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 min-h-[80px]">
              {unplacedItems.map((item, index) => (
                <div
                  key={`unplaced-${index}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item, 'unplaced')}
                  className="bg-white border-2 border-gray-300 rounded-lg p-3 cursor-move hover:border-gray-400 hover:shadow-md transition-all duration-200"
                >
                  <span className="text-sm font-medium text-gray-700">{item.name}</span>
                </div>
              ))}
              {unplacedItems.length === 0 && (
                <div className="w-full text-center text-gray-500 py-4">
                  All items have been sorted! Great job! 🎉
                </div>
              )}
            </div>
          </div>

          {/* Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categories.map((category: any, index: number) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white border-2 border-orange-200 rounded-lg p-4"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, category.name)}
              >
                <h4 className="font-bold text-orange-700 mb-3 text-center">
                  {category.name}
                </h4>
                
                <div className="min-h-[120px] border-2 border-dashed border-orange-300 rounded-lg p-3 bg-orange-50">
                  <div className="space-y-2">
                    {category.items.map((item: any, itemIndex: number) => (
                      <div
                        key={`placed-${itemIndex}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item, category.name)}
                        className={`
                          p-2 rounded border-2 cursor-move transition-all duration-200
                          ${item.originalCategory === category.name
                            ? 'bg-green-100 border-green-300 text-green-700'
                            : 'bg-red-100 border-red-300 text-red-700'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{item.name}</span>
                          {item.originalCategory === category.name && (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {category.items.length === 0 && (
                      <div className="text-center text-gray-400 py-8 text-sm">
                        Drop items here
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-blue-700 text-sm">
              <strong>How to play:</strong> Drag each item from the top box into the category where it belongs. 
              Green items are correctly placed, red items need to be moved to the right category!
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SortingWorksheet;
